<template>
  <div class="container">
    <template v-if="currentStep === stepEnum['Main']">
      <Header
        :title="$t('global.my_requests')"
        hide-action
        :track-event-context="CMP_CONTEXTS.MY_REQUEST"
        :back-callback="handleBack"
      />
      <div v-loading.center="{ isLoading: loading }">
        <div v-if="!isEmpty">
          <div ref="main" class="main">
            <template v-if="displayActiveRequests">
              <span class="title">{{ $t('page.my_request.list.title.active_request') }}</span>
              <div v-if="!sortedOpenRequests.length" class="no-active-msg">
                {{ $t('page.my_request.list.no_active_request') }}
              </div>
              <div
                v-for="request in sortedOpenRequests"
                :key="request.id"
                class="request"
                @click="goDetail(request.id, request, { isOpen: true })"
              >
                <div class="request__title">
                  {{ request.subject }}
                </div>
                <div class="request__body">
                  <span class="info">
                    # {{ request.id }} | <TimeAgo :date="request.updatedAt" />
                    <span v-if="request.status === 'pending' || request.status === 'PENDING'">
                      | {{ $t('page.my_request.list.awaiting_action') }}
                    </span>
                  </span>
                  <span class="status">{{ $t('page.my_request.request_active') }}</span>
                </div>
              </div>
            </template>
            <template v-if="displayInactiveRequests">
              <span class="title">{{ $t('page.my_request.list.title.closed_request') }}</span>
              <div v-if="!sortedClosedRequests.length" class="no-active-msg">
                {{ $t('page.my_request.list.no_closed_request') }}
              </div>
              <div
                v-for="request in sortedClosedRequests"
                :key="request.id"
                class="request"
                @click="goDetail(request.id, request, { isOpen: false })"
              >
                <div class="request__title">
                  {{ request.subject }}
                </div>
                <div class="request__body">
                  <span class="info"> # {{ request.id }} | <TimeAgo :date="request.updatedAt" /> </span>
                  <div class="status-container">
                    <span v-if="showRecentResolvedMark(request)" class="recently-resolved-indicator" />
                    <span class="done status">
                      {{ request.status | capitalize }}
                    </span>
                  </div>
                </div>
              </div>
            </template>
            <div v-if="totalPages > 1" class="pagination">
              <Pagination :total="totalPages" :current="pagination.currentPage" @changePage="handleChangePage" />
            </div>
          </div>
        </div>
        <div v-else class="request__empty">
          <img :src="require('@/assets/img/empty_image.png')" />
          <span>{{ $t('page.my_request.list.no_requests.title') }}</span>
          <span>{{ $t('page.my_request.list.no_requests.subtitle') }}</span>
        </div>
      </div>
    </template>

    <template v-else-if="currentStep === stepEnum['Detail']">
      <MyRequestDetail
        :ticket-id="selectedTicketId"
        :initial-ticket="selectedTicket"
        @back="goMain"
        @update-ticket="getUserTickets"
      />
    </template>
  </div>
</template>

<script lang="ts">
import { Component, Prop } from 'vue-property-decorator';

import * as PleaseApi from '@/common/api/api.pleased';
import * as PleasedModel from '@/common/api/models.pleased';
import Header from '@/components/Header.vue';
import Pagination from '@/components/Pagination.vue';
import TimeAgo from '@/components/TimeAgo.vue';
import { Bridge } from '@/common/bridge';
import { emitOpenMyRequest } from '@/common/eventBus';
import loading from '@/components/loading';
import MyRequestDetail from './MyRequestDetail.vue';
import { UserTicket } from '../../common/api/model';

import { namespace } from 'vuex-class';
import { PleasedUserInfo } from '@/common/types';
import { GA_EVENT_NAME_ENUM } from '@/common/gaHelper';
import LogPageEventMixin, { PAGE_TRACKING, PAGE_EVENTS } from '@/mixins/logPageEvent';
import { mixins } from 'vue-class-component';
import { analytics, syncLogs, CMP_CONTEXTS, BTN_EVTS } from '@/common/logs';
import { getRecentResolvedTicket } from '@/common/userRequest';

const userModule = namespace('user');

enum stepEnum {
  Main,
  Detail,
}

@Component({
  components: {
    Header,
    MyRequestDetail,
    Pagination,
    TimeAgo,
  },
  directives: {
    loading,
  },
  filters: {
    capitalize(word: string) {
      return word[0].toUpperCase() + word.substring(1);
    },
  },
})
export default class MyRequest extends mixins(LogPageEventMixin) {
  isEmpty = false;
  stepEnum = stepEnum;
  selectedTicketId: string = '';
  selectedTicket = {} as UserTicket;
  currentStep = stepEnum['Main'];
  userTickets: PleasedModel.ListTicketDetail[] = [];
  recentResolvedTicket: PleasedModel.ListTicketDetail | null = null;
  loading: boolean = false;
  pagination = {
    total: 0,
    offset: 0,
    limit: 15,
    currentPage: 1,
  };

  @Prop({ default: false }) readonly openDirectly!: boolean;
  @Prop({ default: false }) readonly fromHome!: boolean;

  @userModule.State('pleasedUser') pleasedUser!: PleasedUserInfo;

  readonly [PAGE_TRACKING] = PAGE_EVENTS.MY_REQUESTS_HOME;
  readonly CMP_CONTEXTS = CMP_CONTEXTS;

  mounted() {
    this.getUserTickets();
    this.getRecentResolvedTicketAndReview();
  }
  get sortedOpenRequests() {
    const openTicks = this.userTickets.filter(
      (item) =>
        item.status !== 'closed' && item.status !== 'solved' && item.status !== 'CLOSED' && item.status !== 'RESOLVED'
    );
    return openTicks.sort((a, b) => {
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
  }
  get sortedClosedRequests() {
    const closedTicks = this.userTickets.filter(
      (item) =>
        item.status === 'closed' || item.status === 'solved' || item.status === 'CLOSED' || item.status === 'RESOLVED'
    );
    return closedTicks.sort((a, b) => {
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
  }
  get totalPages() {
    const { total, limit } = this.pagination;
    return Math.ceil(total / limit);
  }
  get displayActiveRequests() {
    const { currentPage } = this.pagination;
    return currentPage === 1 ? true : !!this.sortedOpenRequests.length;
  }
  get displayInactiveRequests() {
    const { limit } = this.pagination;
    return this.sortedClosedRequests.length ? true : this.userTickets.length < limit;
  }
  created() {
    document.title = 'My Requests';
    this.$gtag.event(GA_EVENT_NAME_ENUM.MY_REQUESTS_INIT);

    if (!this.openDirectly) {
      window.history.pushState('forward', '', '#myrequest'); // For handling Back button

      // Listeners have to be used by reference
      const backListener = () => {
        emitOpenMyRequest(false);
        window.removeEventListener('popstate', backListener);
      };
      window.addEventListener('popstate', backListener);
    }
  }
  getUserTickets() {
    this.loading = true;
    return PleaseApi.listTickets({
      sortBy: 'STATUS',
      offset: this.pagination.offset,
      limit: this.pagination.limit,
      order: 'ASC',
      requesterId: this.pleasedUser.userId,
    })
      .then((res) => {
        this.isEmpty = res.total === 0;
        this.pagination.total = res.total;
        this.userTickets = res.data;
        return res;
      })
      .finally(() => {
        this.loading = false;
      });
  }

  async getRecentResolvedTicketAndReview() {
    const data = await getRecentResolvedTicket(this.pleasedUser.userId);

    if (!data || !data.ticket || data.ticketReviews.length > 0) {
      return;
    }

    this.recentResolvedTicket = data.ticket;
  }

  handleChangePage(page: number) {
    analytics.trackClick({
      name: BTN_EVTS.MY_REQUEST_HOME_PAGINATE_BUTTON,
      metadata: { clickedPage: page },
    });

    this.pagination.currentPage = page;
    this.pagination.offset = (page - 1) * this.pagination.limit;
    this.getUserTickets().then(() => {
      this.scrollToTop();
    });
  }
  goMain() {
    this.currentStep = stepEnum['Main'];
  }
  async handleBack() {
    if (this.fromHome) {
      // From home is router
      this.$router.back();
      return;
    }

    if (this.openDirectly) {
      // if the user is come from Sportybet wap/ app, then need to send event to parent
      await syncLogs();
      Bridge.sendToParent('CLOSE');
    } else {
      history.back();
    }
  }

  goDetail(id: string, ticket: UserTicket, { isOpen }: { isOpen: boolean }) {
    analytics.trackClick({
      name: BTN_EVTS.MY_REQUEST_DETAIL_SELECTED,
      selection: ticket.id,
      metadata: { ticketStatus: ticket.status },
    });

    // Go to active conversation.
    if (isOpen) {
      Bridge.openActiveConversation({
        id,
        title: ticket.subject,
        updatedAt: ticket.updatedAt,
      });
    } else {
      this.selectedTicket = ticket;
      this.selectedTicketId = id;
      this.currentStep = stepEnum['Detail'];
    }
  }
  scrollToTop() {
    const main = this.$refs.main as HTMLDivElement;
    main.scrollTop = 0;
  }
  showRecentResolvedMark(ticket: PleasedModel.ListTicketDetail) {
    if (!this.recentResolvedTicket) {
      return false;
    }

    return ticket.id === this.recentResolvedTicket.id;
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variable.scss';

.container {
  background-color: $my-requests-page-background;
  height: 100vh;
  .main {
    height: calc(100vh - #{$header-height});
    overflow-y: scroll;
    position: relative;
    box-sizing: border-box;
    .title {
      font-size: $size-m;
      color: $my-requests-section-title-color;
      margin: 1.6rem 2.2rem;
      @extend .text-bold;
      display: inline-block;
    }
    .no-active-msg {
      width: 100%;
      text-align: center;
      font-size: $size-m2;
    }
    .request {
      font-size: $size-xs;
      margin-bottom: 0.1rem;
      background-color: $my-requests-item-background;
      padding: 1rem 2rem;
      cursor: pointer;
      &__title {
        @extend .text-bold;
        color: $my-requests-item-title-color;
      }
      &__body {
        color: $my-requests-item-subtitle-color;
        @extend.text-regular;
        display: flex;
        align-items: baseline;
        padding: 0.8rem 0 0.3rem 0;

        .status-container {
          position: relative;
          display: flex;
          align-items: center;
        }

        .recently-resolved-indicator {
          position: absolute;
          width: 0.8rem;
          height: 0.8rem;
          background-color: $my-requests-item-status-recently-resolved-color;
          border-radius: 50%;
          left: -1.5rem;
        }

        .info {
          width: 85%;
        }
        .status {
          border-radius: 0.2rem;
          padding: 0.2rem 0.8rem;
          color: $my-requests-item-status-active-color;
          border: $my-requests-item-status-active-color 0.1rem solid;
          &.done {
            color: $my-requests-item-status-inactive-color;
            border: $my-requests-item-status-inactive-color 0.1rem solid;
          }
        }
      }
    }
  }
  .pagination {
    padding: 1.6rem 0;
  }
  .request__empty {
    font-size: $size-l2;
    line-height: 1.56;
    color: $my-requests-item-subtitle-color;
    display: flex;
    flex-direction: column;
    height: calc(100vh - #{$header-height});
    align-items: center;
    justify-content: center;
    @extend .text-bold;
    img {
      height: 16rem;
      width: 16rem;
      margin-bottom: 1.6rem;
    }
  }
}
</style>
